import React, { useEffect, useState } from 'react';
import { Check, Zap, TrendingUp, Award, Building2, Settings2, Loader2, AlertCircle, RefreshCw, Clock } from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import { getSubscriptionStatus, initiateSubscription, verifySubscription, PlanType } from '../../api/services/pgTenantService';

const PRIMARY = '#276859';
const GOLD    = '#FBBD23';

interface SubscriptionStatus {
  plan: string;
  tenant_count: number;
  max_tenants: number | null;
  max_marketplace_contacts: number | null;
  renewal_date: string | null;
  subscription_expired?: boolean;
  is_paid?: boolean;
  trial_active?: boolean;
  trial_days_left?: number | null;
  trial_end_date?: string | null;
}

declare global {
  interface Window { Razorpay: any; }
}

interface Plan {
  id: PlanType;
  name: string;
  price: number | string;
  period: string | null;
  tenants: string;
  color: string;
  bg: string;
  border: string;
  icon: React.ElementType;
  features: string[];
  cta: string;
  highlight: boolean;
  isCustom?: boolean;
}

const PLANS: Plan[] = [
  {
    id: 'starter',
    name: 'Starter',
    price: 399,
    period: '/month',
    tenants: '1 – 50',
    color: '#276859',
    bg: '#f0f7f4',
    border: '#276859',
    icon: Zap,
    features: [
      'Up to 50 active tenants',
      'Unlimited properties',
      'Tenant management dashboard',
      'Rent tracking & reminders',
      'Ticket & rating system',
      '10 marketplace contacts',
    ],
    cta: 'Subscribe — ₹399/mo',
    highlight: false,
  },
  {
    id: 'growth',
    name: 'Growth',
    price: 599,
    period: '/month',
    tenants: '51 – 100',
    color: '#1d6fa4',
    bg: '#eff6ff',
    border: '#1d6fa4',
    icon: TrendingUp,
    features: [
      'Up to 100 active tenants',
      'Everything in Starter',
      'Priority support',
    ],
    cta: 'Subscribe — ₹599/mo',
    highlight: false,
  },
  {
    id: 'pro',
    name: 'Pro',
    price: 1799,
    period: '/month',
    tenants: '101 – 250',
    color: '#b45309',
    bg: '#fffbeb',
    border: GOLD,
    icon: Award,
    features: [
      'Up to 250 active tenants',
      'Everything in Growth',
      'Dedicated support',
    ],
    cta: 'Subscribe — ₹1,799/mo',
    highlight: true,
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 4299,
    period: '/month',
    tenants: '251 – 500',
    color: '#7c3aed',
    bg: '#f5f3ff',
    border: '#7c3aed',
    icon: Building2,
    features: [
      'Up to 500 active tenants',
      'Everything in Pro',
      'Dedicated account manager',
    ],
    cta: 'Subscribe — ₹4,299/mo',
    highlight: false,
  },
  {
    id: 'custom',
    name: 'Custom',
    price: '4,299 + ₹8/tenant',
    period: '/month',
    tenants: '500+',
    color: '#be185d',
    bg: '#fdf2f8',
    border: '#be185d',
    icon: Settings2,
    features: [
      'Unlimited tenants (500+)',
      'Everything in Enterprise',
      '₹8 per tenant beyond 500',
      'Monthly variable billing',
      'Priority enterprise support',
    ],
    cta: 'Get Custom Plan',
    highlight: false,
    isCustom: true,
  },
];

const PLAN_DISPLAY_NAMES: Record<string, string> = {
  free: 'Free Tier',
  starter: 'Starter',
  growth: 'Growth',
  pro: 'Pro',
  enterprise: 'Enterprise',
  custom: 'Custom',
  basic: 'Basic (Legacy)',
  premium: 'Premium (Legacy)',
  active: 'Premium (Legacy)',
  expired: 'Expired',
};

const SubscriptionPage: React.FC = () => {
  const [status, setStatus] = useState<SubscriptionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [processing, setProcessing] = useState<PlanType | null>(null);
  const [successMsg, setSuccessMsg] = useState('');
  const [scriptReady, setScriptReady] = useState(false);

  useEffect(() => {
    if ((window as any).Razorpay) { setScriptReady(true); return; }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => setScriptReady(true);
    document.body.appendChild(script);
  }, []);

  const fetchStatus = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getSubscriptionStatus();
      setStatus(data);
    } catch {
      setError('Failed to load subscription status.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStatus(); }, []);

  const currentPlan = status?.plan ?? 'free';
  const isActive = (planId: string) => currentPlan === planId;

  const fmtDate = (iso: string) =>
    new Date(iso + (iso.includes('T') ? '' : 'T00:00:00'))
      .toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });

  // trial_end_date may be missing if an old API response is cached; compute from days_left as fallback
  const trialEndDateStr: string | null = (() => {
    if (status?.trial_end_date) return status.trial_end_date;
    if (status?.trial_days_left != null) {
      const d = new Date();
      d.setDate(d.getDate() + status.trial_days_left);
      return d.toISOString().slice(0, 10);
    }
    return null;
  })();

  const handleSubscribe = async (plan: Plan) => {
    if (!scriptReady) {
      setError('Payment script not loaded yet. Please wait a moment and try again.');
      return;
    }
    setProcessing(plan.id);
    setError('');

    try {
      const data = await initiateSubscription(plan.id);

      // Custom plan returns a Razorpay Order; other plans return a Subscription
      const isOrder = plan.isCustom || !!data.order_id;
      const razorpayId = data.order_id || data.id || data.subscription_id;
      if (!razorpayId) throw new Error('No payment ID received from server.');

      const options: any = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        name: 'AffordaNest',
        description: `${plan.name} Plan`,
        theme: { color: plan.color },
        modal: { ondismiss: () => setProcessing(null) },
      };

      if (isOrder) {
        options.order_id = razorpayId;
        options.amount = data.amount;
        options.currency = 'INR';
        options.handler = async (response: any) => {
          try {
            await verifySubscription(response.razorpay_order_id || razorpayId, plan.id, true);
            await fetchStatus();
            setSuccessMsg(`${plan.name} plan activated successfully!`);
          } catch {
            setError('Payment succeeded but activation failed. Please contact support.');
          } finally {
            setProcessing(null);
          }
        };
      } else {
        options.subscription_id = razorpayId;
        options.handler = async (response: any) => {
          try {
            await verifySubscription(response.razorpay_subscription_id || razorpayId, plan.id, false);
            await fetchStatus();
            setSuccessMsg(`${plan.name} plan activated successfully!`);
          } catch {
            setError('Payment succeeded but activation failed. Please contact support.');
          } finally {
            setProcessing(null);
          }
        };
      }

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err: any) {
      setError(err?.message || 'Failed to initiate payment. Please try again.');
      setProcessing(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAF8]">
      {/* Header */}
      <div
        className="text-white px-6 py-12 text-center"
        style={{ background: `linear-gradient(135deg, ${PRIMARY} 0%, #2A5248 100%)` }}
      >
        <h1 className="text-3xl font-bold mb-2">Choose Your Plan</h1>
        <p className="text-white/70 text-base max-w-md mx-auto">
          Add unlimited properties for free. Subscribe based on how many tenants you manage.
        </p>

        {!loading && status && (
          <div className="mt-5 inline-flex items-center gap-2 bg-white/15 border border-white/30 rounded-full px-4 py-2">
            <span className="text-sm font-semibold text-white/90">Current Plan:</span>
            <span
              className="text-sm font-bold capitalize px-2.5 py-0.5 rounded-full"
              style={{ backgroundColor: status.trial_active ? '#fde68a' : GOLD, color: PRIMARY }}
            >
              {PLAN_DISPLAY_NAMES[currentPlan] ?? currentPlan}
            </span>
            {status.trial_active && trialEndDateStr && (
              <span className="text-xs text-white/70">
                · Ends {fmtDate(trialEndDateStr)}
                {status.trial_days_left != null && ` (${status.trial_days_left} day${status.trial_days_left === 1 ? '' : 's'} left)`}
              </span>
            )}
            {!status.trial_active && status.renewal_date && (
              <span className="text-xs text-white/60">
                · Renews {fmtDate(status.renewal_date)}
              </span>
            )}
          </div>
        )}
      </div>

      <div className="max-w-6xl mx-auto px-4 py-10">
        {/* Usage bar */}
        {status && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 mb-8">
            <div className="flex flex-wrap gap-6 justify-center">
              <UsageStat
                label="Active Tenants"
                current={status.tenant_count}
                max={status.max_tenants}
              />
            </div>
          </div>
        )}

        {/* Expiry warning */}
        {status?.subscription_expired && (
          <div className="flex items-center gap-2 bg-orange-50 border border-orange-300 rounded-xl p-4 mb-6 text-orange-800 text-sm font-medium">
            <AlertCircle className="h-4 w-4 shrink-0" />
            Your subscription expired on{' '}
            {status.renewal_date ? fmtDate(status.renewal_date) : 'an unknown date'}
            . Adding new tenants is blocked until you renew.
          </div>
        )}

        {/* Free tier trial banner */}
        {status?.trial_active && (
          <div className="flex items-start gap-2 bg-teal-50 border border-teal-300 rounded-xl p-4 mb-6 text-teal-800 text-sm font-medium">
            <Clock className="h-4 w-4 shrink-0 mt-0.5" />
            <span>
              You are on the <strong>Free Tier</strong> — unlimited tenants included.{' '}
              {trialEndDateStr ? (
                <>
                  Your free tier ends on{' '}
                  <strong>{fmtDate(trialEndDateStr)}</strong>
                  {status.trial_days_left != null && (
                    <> ({status.trial_days_left} day{status.trial_days_left === 1 ? '' : 's'} left)</>
                  )}
                  . Subscribe before then to keep managing tenants.
                </>
              ) : (
                'Subscribe to a paid plan to continue after the trial ends.'
              )}
            </span>
          </div>
        )}

        {/* Alerts */}
        {error && (
          <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl p-4 mb-6 text-red-700 text-sm">
            <AlertCircle className="h-4 w-4 shrink-0" />
            {error}
          </div>
        )}
        {successMsg && (
          <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-xl p-4 mb-6 text-green-800 text-sm font-medium">
            <Check className="h-4 w-4 shrink-0" />
            {successMsg}
            <button onClick={fetchStatus} className="ml-auto">
              <RefreshCw className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Plan cards */}
        {loading ? (
          <div className="flex justify-center py-24">
            <Loader2 className="h-10 w-10 animate-spin" style={{ color: PRIMARY }} />
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5">
            {PLANS.map((plan) => {
              const active = isActive(plan.id);
              const Icon = plan.icon;
              return (
                <div
                  key={plan.id}
                  className={cn(
                    'rounded-2xl border-2 p-5 flex flex-col transition-all duration-200 relative',
                    plan.highlight && 'shadow-lg scale-[1.02]',
                    active && 'ring-2 ring-offset-2'
                  )}
                  style={{
                    background: plan.bg,
                    borderColor: active ? plan.color : plan.border,
                    ...(active ? { '--tw-ring-color': plan.color } as any : {}),
                  }}
                >
                  {plan.highlight && (
                    <div
                      className="absolute -top-3.5 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-bold shadow whitespace-nowrap"
                      style={{ backgroundColor: GOLD, color: PRIMARY }}
                    >
                      MOST POPULAR
                    </div>
                  )}

                  {/* Plan header */}
                  <div className="flex items-center gap-2 mb-3">
                    <div className="p-2 rounded-xl" style={{ backgroundColor: `${plan.color}18` }}>
                      <Icon className="h-4 w-4" style={{ color: plan.color }} />
                    </div>
                    <div>
                      <h3 className="font-bold text-base" style={{ color: plan.color }}>{plan.name}</h3>
                      <div className="text-xs text-gray-500">{plan.tenants} tenants</div>
                    </div>
                  </div>

                  {/* Price */}
                  <div className="mb-3">
                    {typeof plan.price === 'number' ? (
                      <div className="flex items-baseline gap-0.5">
                        <span className="text-2xl font-bold" style={{ color: plan.color }}>₹{plan.price.toLocaleString('en-IN')}</span>
                        <span className="text-xs text-gray-400">{plan.period}</span>
                      </div>
                    ) : (
                      <div>
                        <span className="text-base font-bold" style={{ color: plan.color }}>₹{plan.price}</span>
                        <span className="text-xs text-gray-400">{plan.period}</span>
                      </div>
                    )}
                  </div>

                  {/* Features */}
                  <ul className="space-y-1.5 flex-1 mb-4">
                    {plan.features.map((f) => (
                      <li key={f} className="flex items-start gap-1.5 text-xs text-gray-700">
                        <Check className="h-3.5 w-3.5 mt-0.5 shrink-0" style={{ color: plan.color }} />
                        {f}
                      </li>
                    ))}
                  </ul>

                  {/* CTA */}
                  <Button
                    className="w-full font-bold py-2 rounded-xl text-sm"
                    disabled={active || processing !== null}
                    style={
                      active
                        ? { backgroundColor: '#e5e7eb', color: '#6b7280', cursor: 'default' }
                        : { backgroundColor: plan.color, color: 'white' }
                    }
                    onClick={() => !active && handleSubscribe(plan)}
                  >
                    {processing === plan.id ? (
                      <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> Processing…</>
                    ) : active ? (
                      '✓ Current Plan'
                    ) : (
                      plan.cta
                    )}
                  </Button>
                </div>
              );
            })}
          </div>
        )}

        {/* Pricing table */}
        <div className="mt-12 bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <h2 className="font-bold text-lg" style={{ color: PRIMARY }}>Plan Comparison</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left px-6 py-3 text-gray-500 font-medium">Feature</th>
                  <th className="px-3 py-3 text-center font-semibold" style={{ color: '#276859' }}>Starter</th>
                  <th className="px-3 py-3 text-center font-semibold" style={{ color: '#1d6fa4' }}>Growth</th>
                  <th className="px-3 py-3 text-center font-semibold" style={{ color: '#b45309' }}>Pro</th>
                  <th className="px-3 py-3 text-center font-semibold" style={{ color: '#7c3aed' }}>Enterprise</th>
                  <th className="px-3 py-3 text-center font-semibold" style={{ color: '#be185d' }}>Custom</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['Monthly price',          '₹399',  '₹599',  '₹1,799', '₹4,299', '₹4,299 + ₹8/tenant'],
                  ['Tenant limit',           '50',    '100',   '250',    '500',    'Unlimited'],
                  ['Properties',             '∞',     '∞',     '∞',      '∞',      '∞'],
                  ['Tenant management',      '✓',     '✓',     '✓',      '✓',      '✓'],
                  ['Rent tracking',          '✓',     '✓',     '✓',      '✓',      '✓'],
                  ['Ticket & rating system', '✓',     '✓',     '✓',      '✓',      '✓'],
                  ['Marketplace contacts',   '10',    '25',    '75',     '150',    '∞'],
                  ['Priority support',       '—',     '✓',     '✓',      '✓',      '✓'],
                  ['Dedicated manager',      '—',     '—',     '—',      '✓',      '✓'],
                ].map(([feature, ...vals], i) => (
                  <tr key={i} className={i % 2 === 0 ? 'bg-gray-50/50' : ''}>
                    <td className="px-6 py-2.5 text-gray-700 font-medium">{feature}</td>
                    {vals.map((v, j) => (
                      <td key={j} className="px-3 py-2.5 text-center text-gray-600">{v}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Custom plan billing note */}
        <div className="mt-6 bg-pink-50 border border-pink-200 rounded-2xl p-5 text-sm text-pink-900">
          <strong>Custom plan billing:</strong> Your monthly charge = ₹4,299 (base for first 500 tenants) + ₹8 × every additional tenant. The exact amount is calculated at the time you initiate payment each month based on your current tenant count.
        </div>
      </div>
    </div>
  );
};

const UsageStat: React.FC<{
  label: string;
  current: number | null;
  max: number | null;
}> = ({ label, current, max }) => {
  const pct = current !== null && max !== null && max > 0 ? Math.min((current / max) * 100, 100) : null;
  const displayMax = max === null ? 'Unlimited' : max === 0 ? 'No plan active' : String(max);

  return (
    <div className="flex-1 min-w-[220px]">
      <div className="flex justify-between items-center mb-1.5">
        <span className="text-sm font-medium text-gray-600">{label}</span>
        <span className="text-sm font-bold" style={{ color: PRIMARY }}>
          {current !== null ? `${current} / ` : ''}{displayMax}
        </span>
      </div>
      {pct !== null && (
        <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all"
            style={{
              width: `${pct}%`,
              backgroundColor: pct >= 90 ? '#ef4444' : pct >= 70 ? GOLD : PRIMARY,
            }}
          />
        </div>
      )}
    </div>
  );
};

export default SubscriptionPage;
